import { useConnectionSpecContext } from '@common/Fabric/Connections/hooks';
import { useUpdateOrchestrationFabricDetails } from '@common/Fabric/OrchestrationFabric/utils';
import { useCanEditFabric } from '@common/Fabric/useCanEditFabric';
import { PageHeader } from '@common/Header/PageHeader';
import { useBackendLanguage } from '@common/language';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { UpdateSuccessArgs } from '@pages/Metadata/components/EntityInfo/types';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { Stack, StackItem, TabPane, Tabs } from '@prophecy/ui-v3';
import { StyledTabsContentWrap, StyledTabsList } from '@prophecy/ui-v3/Tabs/styled';
import { compose } from '@prophecy/utils/redux';
import { noop } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { createStore } from 'redux';
import styled from 'styled-components';

import { SQL_PROVIDER } from '../../constants/sql';
import { ExecutionServiceProvider } from '../../context/execution';
import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { useUser } from '../../context/user';
import { useExecutionServiceInstance } from '../../data/executionHooks';
import { InfoEntity } from '../Metadata/components/EntityInfo';
import { AttachFabric } from '../Workflow/AttachFabric/AttachFabric';
import { ComponentRunContext, useRunDataContext } from '../Workflow/AttachFabric/componentRunhook';
import AdvancedTab, { CopilotSettings } from './advanced';
import { ConnectionsTab } from './common/ConnectionsTab';
import FabricCrudWrapper from './common/FabricCrudWrapper';
import { FabricInfoFormContextProvider } from './common/FabricInfoFormContext';
import { withInitialFabricCopy } from './common/InitialFabricCopyContext';
import { ProviderTab } from './common/ProviderTab';
import { withTestConnectionProvider } from './common/TestConnectionContext';
import { FabricSchema, FABRIC_TAB_LABELS } from './constants';
import useFormContext from './hooks/useFormContext';
import { useIsFabricAdmin } from './hooks/useIsFabricAdmin';
import useIsOrchestrationFabric from './hooks/useIsOrchestrationFabric';
import useIsUnifiedFabric from './hooks/useIsUnifiedFabric';
import useLoadFabricData from './hooks/useLoadFabricData';
import { IndexerTab } from './IndexerTab';
import { JobsTab } from './JobsTab';
import { OrchestrationSecretTab } from './secrets/OrchestrationSecretTab';
import { SparkSecretsTab } from './secrets/SparkSecretsTab';
import { Root } from './styles';
import { FABRIC_TABS, FabricProviderType, SPARK_PROVIDER } from './types';

const TabContainer = styled(Stack)`
  margin: ${theme.spaces.x64} auto ${theme.spaces.x32};
  padding: ${theme.spaces.x32} 0 0;
  background-color: ${theme.colors.white};
  height: calc(100% - 92px);
  width: 900px;
`;

const StyledTabs = styled(Tabs)`
  ${StyledTabsList} {
    padding: 0 ${theme.spaces.x40};
  }

  ${StyledTabsContentWrap} {
    padding: 0 ${theme.spaces.x40};
  }
`;

const StyledInfoEntity = styled(InfoEntity)`
  padding: 0 0 ${theme.spaces.x32};
` as typeof InfoEntity;

const StyledHeaderContainer = styled.div`
  min-height: ${theme.sizes.x48};
`;

type Props = {
  isReadMode?: boolean;
};

const UpdateFabric = withInitialFabricCopy(
  withTestConnectionProvider(({ isReadMode = false }: Props) => {
    const { uid: fabricId = '', tab = FABRIC_TABS.basic } =
      useParams<ExtractUrlPathVariables<typeof Private_Routes.Fabric.tab.url>>();

    const executionService = useExecutionServiceInstance();
    const { track } = useMixPanel();
    const formContexts = useFormContext(fabricId);
    const globalConf = useGlobalVariables();
    const {
      isLoading: isFabricLoading,
      refreshWarehouseConnection,
      setFormUpdateLock
    } = useLoadFabricData(fabricId, formContexts);
    const { isAdmin } = useIsFabricAdmin(formContexts);

    const fabricName = formContexts[FABRIC_TABS.basic].watch('name');
    const fabricProviderType = formContexts[FABRIC_TABS.providers].watch('fabricProviderType');

    const isUnifiedFabric = useIsUnifiedFabric(formContexts);
    const fabricProviderTypesV2 = formContexts[FABRIC_TABS.providers].watch('initialFabricProviderTypes');
    const provider = formContexts[FABRIC_TABS.providers].watch('provider');

    // Sync provider to ConnectionSpecProvider
    const { setProviderType } = useConnectionSpecContext();
    useEffect(() => {
      setProviderType(provider);
    }, [provider, setProviderType]);

    const fabricData = formContexts[FABRIC_TABS.providers].watch('fabricData');
    const teamId = formContexts[FABRIC_TABS.basic].watch('ownerUID');
    const { refetch: refetchUserData } = useUser();

    const { updateOrchestrationFabricDetails } = useUpdateOrchestrationFabricDetails();

    const { isOrchestrationFabric, isOldOrchestrationFabric } = useIsOrchestrationFabric(formContexts);

    const isUnifiedDbxFabric = isUnifiedFabric && provider === SPARK_PROVIDER.DATABRICKS;
    const isUnifiedSnowflakeFabric = isUnifiedFabric && provider === SQL_PROVIDER.Snowflake;
    const isUnifiedBigQueryFabric = isUnifiedFabric && provider === SQL_PROVIDER.BigQuery;

    const isUnifiedOrchestrationFabric =
      isUnifiedFabric && Boolean(fabricProviderTypesV2?.includes(FabricProviderType.Orchestration));

    const isUnifiedSparkFabric = isUnifiedFabric && Boolean(fabricProviderTypesV2?.includes(FabricProviderType.Spark));
    const isUnifiedDbxSparkFabric = isUnifiedDbxFabric && isUnifiedSparkFabric;
    const isUnifiedDbxOrchFabric = isUnifiedDbxFabric && isUnifiedOrchestrationFabric;
    const isUnifiedSnowflakeOrchFabric = isUnifiedSnowflakeFabric && isUnifiedOrchestrationFabric;
    // showing BQ indexer tab for SAP only since SQL Warehouse connection isn't shown (enterprise always shows it), that's why we show it for SAP only
    const isSapBigQueryOrch = globalConf.isSapEnabled && isUnifiedBigQueryFabric && isUnifiedOrchestrationFabric;

    const onUpdateSuccess = (args: UpdateSuccessArgs) => {
      if (isOrchestrationFabric) {
        if (args.type === 'entity' && args.entityFieldName === 'name') {
          // TODO: Revert the fabric name to the original name incase this call fails
          updateOrchestrationFabricDetails({
            fabricId,
            name: args.entityFieldValue,
            teamId
          });
          refetchUserData();
        } else if (args.type === 'entity' && args.entityFieldName === Entity.Team) {
          updateOrchestrationFabricDetails({
            fabricId,
            teamId: args.entityFieldValue,
            name: fabricName
          });
          refetchUserData();
        }
      }
    };
    const {
      actions: { hasFabricCrudOperationAccess }
    } = useCanEditFabric();

    // allow users to edit if fabric is unified fabric
    const allowEditFabric = hasFabricCrudOperationAccess(teamId, isOrchestrationFabric).allowed;

    const allowEditFabricForProvidersTab = allowEditFabric || isUnifiedDbxFabric || isUnifiedSnowflakeFabric;

    const isUnifiedSparkOnlyDbxFabric = isUnifiedDbxSparkFabric && fabricProviderTypesV2?.length === 1;

    const isUnifiedSparkPmManagedFabric = isUnifiedSparkFabric && provider === SPARK_PROVIDER.PROPHECY;
    // in unified fabric flow, hide connections for spark only dbx fabric or spark pm managed fabric
    const hideConnections = isUnifiedSparkOnlyDbxFabric || isUnifiedSparkPmManagedFabric;
    const language = useBackendLanguage();

    return (
      <ExecutionServiceProvider value={executionService}>
        <ComponentRunContext.Provider value={useRunDataContext()}>
          <Root>
            <StyledHeaderContainer>
              <PageHeader
                crumbs={[
                  {
                    category: 'Fabric',
                    label: fabricName,
                    url: Private_Routes.Fabric.home.getUrl({ uid: fabricId })
                  }
                ]}
                banner={null}>
                {fabricProviderType === FabricProviderType.Spark && !isFabricLoading && (
                  <AttachFabric
                    allowFabricSelection={false}
                    setSessionUsed={noop}
                    hidden={tab !== FABRIC_TABS.secrets}
                    attachmentSource='Secrets'
                    disabled={false}
                    fabricId={fabricId}
                    setFabric={noop}
                    isPipelineOnboardingEnabled={false}
                    language={language}
                  />
                )}
              </PageHeader>
            </StyledHeaderContainer>

            <StackItem grow='1'>
              <TabContainer>
                <StyledTabs
                  activeKey={tab}
                  onChange={(tab) => {
                    track(EntityEvents.common.metadata.tabChange, {
                      fabricId,
                      tab,
                      name: fabricName,
                      entity: Entity.Fabric
                    });
                  }}>
                  <TabPane
                    disabled={isReadMode}
                    key={FABRIC_TABS.basic}
                    tab={FABRIC_TAB_LABELS[FABRIC_TABS.basic]}
                    elementType='link'
                    to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.basic })}>
                    <FabricInfoFormContextProvider disableTeamSelection={isOrchestrationFabric}>
                      <StyledInfoEntity
                        showLabel
                        schema={FabricSchema}
                        variables={{ uid: fabricId }}
                        onUpdateSuccess={onUpdateSuccess}
                        readOnly={!allowEditFabric}
                      />
                    </FabricInfoFormContextProvider>
                  </TabPane>

                  <TabPane
                    disabled={isReadMode}
                    key={FABRIC_TABS.providers}
                    tab={FABRIC_TAB_LABELS[FABRIC_TABS.providers]}
                    elementType='link'
                    to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.providers })}>
                    <ProviderTab
                      fabricId={fabricId}
                      formContexts={formContexts}
                      isLoading={isFabricLoading}
                      disabledMessage={
                        !allowEditFabricForProvidersTab ? "You don't have access to update this fabric" : ''
                      }
                      refreshWarehouseConnection={refreshWarehouseConnection}
                      setFormUpdateLock={setFormUpdateLock}
                    />
                  </TabPane>
                  {(isUnifiedDbxOrchFabric || isUnifiedSnowflakeOrchFabric || isSapBigQueryOrch) && (
                    <TabPane
                      disabled={isReadMode}
                      key={FABRIC_TABS.indexer}
                      tab={FABRIC_TAB_LABELS[FABRIC_TABS.indexer]}
                      elementType='link'
                      to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.indexer })}>
                      <IndexerTab fabricId={fabricId} teamId={teamId} />
                    </TabPane>
                  )}

                  {!hideConnections && (
                    <TabPane
                      disabled={isReadMode}
                      key={FABRIC_TABS.connections}
                      tab={FABRIC_TAB_LABELS[FABRIC_TABS.connections]}
                      elementType='link'
                      to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.connections })}>
                      <ConnectionsTab
                        disabled={!allowEditFabric}
                        formContexts={formContexts}
                        isLoading={isFabricLoading}
                      />
                    </TabPane>
                  )}

                  {/* Same Secrets tab is used for Spark/Orchestration fabric in unified fabric experience */}
                  {(globalConf.isSecretManagerEnabled &&
                    fabricData &&
                    fabricProviderType === FabricProviderType.Spark) ||
                  isUnifiedOrchestrationFabric ? (
                    <TabPane
                      disabled={isReadMode}
                      key={FABRIC_TABS.secrets}
                      tab={FABRIC_TAB_LABELS[FABRIC_TABS.secrets]}
                      elementType='link'
                      to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.secrets })}>
                      <SparkSecretsTab
                        isUnifiedOrchestrationFabric={isUnifiedOrchestrationFabric}
                        formContexts={formContexts}
                        fabricId={fabricId}
                      />
                    </TabPane>
                  ) : (
                    // Secrets tab for old orchestration fabric (not unified fabric experience) */}
                    isOldOrchestrationFabric && (
                      <TabPane
                        disabled={isReadMode}
                        key={FABRIC_TABS.secrets}
                        tab={FABRIC_TAB_LABELS[FABRIC_TABS.secrets]}
                        elementType='link'
                        to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.secrets })}>
                        <OrchestrationSecretTab isAdmin={isAdmin} />
                      </TabPane>
                    )
                  )}

                  {isUnifiedDbxSparkFabric && (
                    <TabPane
                      disabled={isReadMode}
                      key={FABRIC_TABS.jobs}
                      tab={FABRIC_TAB_LABELS[FABRIC_TABS.jobs]}
                      elementType='link'
                      to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.jobs })}>
                      <JobsTab
                        formContexts={formContexts}
                        isLoading={isFabricLoading}
                        fabricId={fabricId}
                        teamId={teamId}
                      />
                    </TabPane>
                  )}

                  {isAdmin && fabricProviderType === FabricProviderType.Spark && (
                    <TabPane
                      key={FABRIC_TABS.advanced}
                      tab={FABRIC_TAB_LABELS[FABRIC_TABS.advanced]}
                      elementType='link'
                      to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.advanced })}>
                      <AdvancedTab disabled={!allowEditFabric} formContexts={formContexts} fabricId={fabricId} />
                    </TabPane>
                  )}

                  {isAdmin && isOldOrchestrationFabric && (
                    <TabPane
                      key={FABRIC_TABS.advanced}
                      tab={FABRIC_TAB_LABELS[FABRIC_TABS.advanced]}
                      elementType='link'
                      to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: FABRIC_TABS.advanced })}>
                      <CopilotSettings fabricId={fabricId} />
                    </TabPane>
                  )}
                </StyledTabs>
              </TabContainer>
            </StackItem>
          </Root>
        </ComponentRunContext.Provider>
      </ExecutionServiceProvider>
    );
  })
);

const WrappedUpdateFabric = () => {
  const { uid: fabricId = '' } = useParams<ExtractUrlPathVariables<typeof Private_Routes.Fabric.tab.url>>();

  const store = useMemo(() => {
    // to set fabricId in useSQLFabrics for OrchestrationAuthProvider to work
    // @ts-ignore FIXME:
    return createStore((state) => state, { currentGraph: { metainfo: { fabricId } } }, compose('UpdateFabric'));
  }, [fabricId]);

  return (
    <FabricCrudWrapper enableAdminJwt store={store}>
      <UpdateFabric />
    </FabricCrudWrapper>
  );
};

export default WrappedUpdateFabric;
